import { useEffect, useRef, useState } from 'react';

const TICK_MS = 30;
const MIN_CHARS_PER_TICK = 2;
const CATCH_UP_DIVISOR = 5;

const revealUpToWordBoundary = (target: string, budgetedLength: number): string => {
    const candidate = target.slice(0, budgetedLength);
    const lastBoundary = Math.max(candidate.lastIndexOf(' '), candidate.lastIndexOf('\n'));
    return lastBoundary === -1 ? candidate : candidate.slice(0, lastBoundary + 1);
};

/**
 * Reveals `targetText` progressively instead of popping in it whenever
 * it changes, causing a streaming reply to visually "type"
 */
export const useSmoothedText = (targetText: string): string => {
    const [displayedText, setDisplayedText] = useState(targetText);
    const displayedRef = useRef(targetText);
    const targetRef = useRef(targetText);
    targetRef.current = targetText;

    useEffect(() => {
        // This will notice when a new message comes in by comparing the length against
        // the text currently displayed from a stream and push the rendering to catch up
        if (targetText.length < displayedRef.current.length) {
            displayedRef.current = targetText;
            setDisplayedText(targetText);
        }
    }, [targetText]);

    useEffect(() => {
        // Used to track when to render raw characters as the response
        // has stalled for several ticks in the middle of a word
        let stalledTicks = 0;

        const interval = setInterval(() => {
            const current = displayedRef.current;
            const target = targetRef.current;
            if (current.length >= target.length) {
                stalledTicks = 0;
                return;
            }

            const remainingChars = target.length - current.length;
            const charsToReveal = Math.max(MIN_CHARS_PER_TICK, Math.ceil(remainingChars / CATCH_UP_DIVISOR));
            const budgetedLength = current.length + charsToReveal;

            let next = budgetedLength >= target.length ? target : revealUpToWordBoundary(target, budgetedLength);

            if (next.length > current.length) {
                stalledTicks = 0;
            } else {
                stalledTicks += 1;
                if (stalledTicks > 3) {
                    next = target.slice(0, budgetedLength);
                } else {
                    return;
                }
            }

            displayedRef.current = next;
            setDisplayedText(next);
        }, TICK_MS);

        return () => clearInterval(interval);
    }, []);

    return displayedText;
};
